import _ from "lodash";
import { useQueryClient } from "@tanstack/react-query";
import { ETransactionType } from "../helpers/constants";
import { useCallback, useEffect, useMemo } from "react";
import { formatCurrency, formatEasyDate } from "../helpers/utils";
import useCurrentAccount from "./useCurrentAccount";
import moment from "moment";
import useMainFilter from "./useMainFilter";
import { CACHE_KEYS } from "src/constants/cache";
import useAllTransactions from "./useAllTransactions";

const MAX_DATES = 7;

const toArray = (value: any): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const filterLocalTransactions = (transactions: any[] = [], mainFilter: any) => {
  const {
    dateFrom,
    dateTo,
    clientProvider,
    type,
    status,
    paymentMethod,
    tags,
    category,
    account,
  } = mainFilter || {};

  const clientProviderList = toArray(clientProvider);
  const statusList = toArray(status);
  const paymentMethodList = toArray(paymentMethod);
  const tagsList = toArray(tags);
  const categoryList = toArray(category);

  return transactions
    .filter((transaction) => {
      if (transaction.archived) return false;
      if (transaction.deleted) return false;
      if (transaction.shared) return false;

      if (dateFrom && transaction.date < dateFrom) return false;
      if (dateTo && transaction.date > dateTo) return false;

      if (account && transaction.account !== account) return false;
      if (type && transaction.type !== type) return false;

      if (
        clientProviderList.length &&
        !clientProviderList.includes(transaction.payTo)
      ) {
        return false;
      }

      if (statusList.length && !statusList.includes(transaction.status)) {
        return false;
      }

      if (
        paymentMethodList.length &&
        !paymentMethodList.includes(transaction.paymentMethod)
      ) {
        return false;
      }

      if (categoryList.length && !categoryList.includes(transaction.category)) {
        return false;
      }

      if (tagsList.length) {
        const transactionTags = transaction.tags || [];

        const hasTag = transactionTags.some((tag: string) =>
          tagsList.includes(tag),
        );

        if (!hasTag) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = `${a.date || ""} ${a.time || ""}`;
      const dateB = `${b.date || ""} ${b.time || ""}`;

      return dateB.localeCompare(dateA);
    });
};

export default function useFilteredTransactions() {
  const { mainFilter, setMainFilter } = useMainFilter();
  const { currencySymbol } = useCurrentAccount();
  const queryClient = useQueryClient();

  const {
    allTransactions,
    isLoadingAllTransactions,
    isRefetchingAllTransactions,
  } = useAllTransactions();

  const transactionsData = useMemo(() => {
    return filterLocalTransactions(allTransactions || [], mainFilter);
  }, [allTransactions, mainFilter]);

  const transactionsDataWithoutTypeFilter = useMemo(() => {
    return filterLocalTransactions(allTransactions || [], {
      ...mainFilter,
      type: "",
    });
  }, [allTransactions, mainFilter]);

  const lastDates = useMemo(() => {
    return _.take(_.uniq(transactionsData.map((t: any) => t.date)), MAX_DATES);
  }, [transactionsData]);

  const latestTransactions = useMemo(() => {
    return _.filter(transactionsData, (transaction) =>
      lastDates?.includes(transaction?.date),
    );
  }, [transactionsData, lastDates]);

  const latestTransactionsSummary = useMemo(() => {
    const groupedByDate = _.groupBy(latestTransactions, "date");

    return Object.keys(groupedByDate).map((date) => {
      const transactions = groupedByDate[date];

      const income = _.sumBy(
        transactions.filter((t: any) => t.type === ETransactionType.INCOME),
        (t: any) => parseFloat(t.total) || 0,
      );

      const expenses = _.sumBy(
        transactions.filter((t: any) => t.type === ETransactionType.EXPENSE),
        (t: any) => parseFloat(t.total) || 0,
      );

      return {
        date,
        income,
        expenses,
        formattedDate: formatEasyDate({ dateToFormat: date }),
        formattedIncome: formatCurrency({
          value: income,
          currency: currencySymbol,
        }),
        formattedExpenses: formatCurrency({
          value: expenses,
          currency: currencySymbol,
        }),
        difference: income - expenses,
        differenceFormatted: formatCurrency({
          value: income - expenses,
          currency: currencySymbol,
        }),
      };
    });
  }, [latestTransactions, currencySymbol]);

  const groupedByMonthAndYear = useMemo(() => {
    return _(transactionsData)
      .groupBy((t: any) =>
        moment(t.date).format(mainFilter.year ? "YYYY-MM" : "MM"),
      )
      .mapValues((entries) => ({
        INCOME: _.sumBy(
          entries.filter((item: any) => item.type === ETransactionType.INCOME),
          (item: any) => Number(item.total),
        ),
        EXPENSE: _.sumBy(
          entries.filter((item: any) => item.type === ETransactionType.EXPENSE),
          (item: any) => Number(item.total),
        ),
        COUNT: entries.length,
      }))
      .value();
  }, [transactionsData, mainFilter.year]);

  const transactionsFilteredByYear = useMemo(() => {
    if (mainFilter.year) {
      return transactionsData.filter((transaction: any) => {
        return (
          moment(transaction?.date).format("YYYY") === String(mainFilter.year)
        );
      });
    }

    return transactionsData;
  }, [transactionsData, mainFilter.year]);

  const transactionsFilteredByYearWithoutTypeFilter = useMemo(() => {
    if (mainFilter.year) {
      return transactionsDataWithoutTypeFilter.filter((transaction: any) => {
        return (
          moment(transaction?.date).format("YYYY") === String(mainFilter.year)
        );
      });
    }

    return transactionsDataWithoutTypeFilter;
  }, [transactionsDataWithoutTypeFilter, mainFilter.year]);

  const uniqueYears = useMemo(() => {
    return _.chain(transactionsDataWithoutTypeFilter)
      .map((t: any) => moment(t.date).format("YYYY"))
      .uniq()
      .orderBy([(year) => Number(year)], ["desc"])
      .value();
  }, [transactionsDataWithoutTypeFilter]);

  useEffect(() => {
    if (!mainFilter.year) return;

    if (!uniqueYears.length) {
      setMainFilter({ year: "" });
    } else {
      const selectedYear = String(mainFilter.year);

      if (!uniqueYears.includes(selectedYear)) {
        setMainFilter({ year: "" });
      }
    }
  }, [mainFilter.year, uniqueYears, setMainFilter]);

  const getFilteredTransactionById = useCallback(
    ({ transactionId }: { transactionId: string }) => {
      return _.find(transactionsData, { invoice: transactionId });
    },
    [transactionsData],
  );

  const refetchFilteredQueries = async () => {
    return await Promise.allSettled([
      queryClient.refetchQueries({
        queryKey: [CACHE_KEYS.ALL_TRANSACTIONS],
      }),
    ]);
  };

  return {
    filteredTransactions: transactionsData,
    isFilteringTransactions: isLoadingAllTransactions,
    refetchFilteredQueries,
    latestTransactionsSummary,
    monthlyTransactionsSummary: groupedByMonthAndYear,
    uniqueYears,
    getFilteredTransactionById,
    transactionsFilteredByYear,
    transactionsFilteredByYearWithoutTypeFilter,
    isRefetchingFilteredTransactions: isRefetchingAllTransactions,
  };
}
